/**
 * Offline Banner - Componente de estado de conexión
 * Muestra/oculta banner según navigator.onLine
 * Emite eventos custom: 'app:offline' y 'app:online'
 */

import { show, hide, el } from './dom.js';

// Eventos custom para que otros módulos reaccionen a cambios de conectividad
const EVENT_OFFLINE = 'app:offline';
const EVENT_ONLINE = 'app:online';

/**
 * Inicializa el banner offline
 * @param {string} [bannerSelector='#offline-banner'] - Selector CSS del banner
 * @returns {Object} API para control manual: { isOnline, destroy }
 */
export const initOfflineBanner = (bannerSelector = '#offline-banner') => {
  const banner = el(bannerSelector);
  
  if (!banner) {
    console.warn('[OfflineBanner] Banner no encontrado en el DOM');
    return null;
  }
  
  /**
   * Actualiza la UI según el estado de conexión
   */
  const updateStatus = () => {
    const isOnline = navigator.onLine;
    
    if (isOnline) {
      hide(banner);
      document.dispatchEvent(new CustomEvent(EVENT_ONLINE, {
        detail: { timestamp: Date.now() }
      }));
      console.log('[OfflineBanner] ✅ Conexión restaurada');
    } else {
      show(banner);
      document.dispatchEvent(new CustomEvent(EVENT_OFFLINE, {
        detail: { timestamp: Date.now() }
      }));
      console.log('[OfflineBanner] ⚠️ Modo offline activado');
    }
    
    // Actualizar atributo ARIA para screen readers
    banner.setAttribute('aria-hidden', isOnline ? 'true' : 'false');
  };
  
  // Listeners de conectividad
  const handleOnline = () => updateStatus();
  const handleOffline = () => updateStatus();
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Estado inicial
  updateStatus();
  
  // API pública
  return {
    /**
     * Verifica si hay conexión activa
     * @returns {boolean}
     */
    isOnline: () => navigator.onLine,
    
    /**
     * Remueve listeners (cleanup)
     */
    destroy: () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      hide(banner);
    }
  };
};

/**
 * Suscribe a eventos de cambio de conectividad
 * @param {function} onOnline - Callback cuando se restaura conexión
 * @param {function} onOffline - Callback cuando se pierde conexión
 * @returns {function} Función para unsubscribe
 */
export const onConnectivityChange = (onOnline, onOffline) => {
  const handleOnline = (e) => onOnline?.(e.detail);
  const handleOffline = (e) => onOffline?.(e.detail);
  
  document.addEventListener(EVENT_ONLINE, handleOnline);
  document.addEventListener(EVENT_OFFLINE, handleOffline);
  
  return () => {
    document.removeEventListener(EVENT_ONLINE, handleOnline);
    document.removeEventListener(EVENT_OFFLINE, handleOffline);
  };
};

export { EVENT_OFFLINE, EVENT_ONLINE };
